import { apiFetch, localStore } from "./client";
import type { Ticket, Severity, WaitingOn, WorkflowStage } from "../types";

const RESOLVER_GROUP = "WP Incident Management";
const TASKEI_ROOM_ID = "7ec055e6-e7ae-41ce-8b75-600620586f07";
const TASKEI_SPRINT_ID = "e89bef2e-fe3b-451f-b64e-adaf2f981337";
const PIRT_TEAM_MEMBERS = ["folaok", "tjmalv"];

// EU/GDPR countries for jurisdiction detection
const EU_COUNTRIES = new Set([
  "austria", "belgium", "bulgaria", "croatia", "cyprus", "czech republic",
  "denmark", "estonia", "finland", "france", "germany", "greece", "hungary",
  "ireland", "italy", "latvia", "lithuania", "luxembourg", "malta",
  "netherlands", "poland", "portugal", "romania", "slovakia", "slovenia",
  "spain", "sweden", "united kingdom", "turkey", "norway", "iceland",
  "liechtenstein", "switzerland",
]);

function daysBetween(dateStr: string): number {
  const then = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24));
}

function detectEu(text: string): boolean {
  const lower = text.toLowerCase();
  return Array.from(EU_COUNTRIES).some((country) => lower.includes(country)) ||
    lower.includes("gdpr") ||
    lower.includes("dpa") ||
    lower.includes("dim intake");
}

function mapSeverity(sev: string): Severity {
  if (sev.includes("1") || sev.includes("2")) return "SEV_2";
  if (sev.includes("3")) return "SEV_3";
  if (sev.includes("4")) return "SEV_4";
  return "SEV_5";
}

interface SimtTicket {
  ticketId: string;
  title: string;
  severity: string;
  status: string;
  assignee?: { value: string };
  createDate: string;
  lastUpdatedDate: string;
  description?: string;
  tags?: Array<{ tagId: string }>;
}

/**
 * Fetch open tickets from our Lambda proxy (SIM-T + Taskei).
 */
export async function fetchSimtTickets(): Promise<Ticket[]> {
  const localState = localStore.get<Record<string, Partial<Ticket>>>("ticketState", {});

  // Fetch SIM-T tickets
  let simtTickets: Ticket[] = [];
  try {
    const simtResponse = await apiFetch<{ tickets?: SimtTicket[]; issues?: SimtTicket[] }>("/tickets");
    const rawTickets = simtResponse.tickets || simtResponse.issues || [];

    simtTickets = rawTickets.map((t) => {
      const state = localState[t.ticketId] || {};
      const description = t.description || "";
      const isEu = detectEu(description);
      const daysOpen = daysBetween(t.createDate);
      const daysStale = daysBetween(t.lastUpdatedDate);

      return {
        ticketId: t.ticketId,
        source: "SIM_T" as const,
        taskeiTaskId: state.taskeiTaskId,
        title: t.title,
        severity: mapSeverity(t.severity),
        status: t.status,
        workflowStage: (state.workflowStage || "WP Triage") as WorkflowStage,
        assignee: t.assignee?.value || "Unassigned",
        waitingOn: (state.waitingOn || "none") as WaitingOn,
        createdAt: t.createDate,
        lastUpdatedAt: t.lastUpdatedDate,
        daysOpen,
        daysStale,
        isEu,
        gdprDeadline: isEu
          ? new Date(new Date(t.createDate).getTime() + 72 * 60 * 60 * 1000).toISOString()
          : undefined,
        description,
        tags: t.tags?.map((tag) => tag.tagId) || [],
      };
    });
  } catch (error) {
    console.warn("Failed to fetch SIM-T tickets:", error);
  }

  // Fetch Taskei tasks
  let taskeiTickets: Ticket[] = [];
  try {
    const taskeiResponse = await apiFetch<{ tasks?: any[] }>("/taskei/tasks");
    const rawTasks = taskeiResponse.tasks || [];

    taskeiTickets = rawTasks.map((t: any) => {
      const state = localState[t.id || t.taskId] || {};
      const description = t.description || "";
      const isEu = detectEu(description);
      const createdAt = t.createDate || t.createdAt || new Date().toISOString();
      const updatedAt = t.lastUpdatedDate || t.lastUpdatedAt || createdAt;

      return {
        ticketId: t.shortId || t.id || t.taskId,
        source: "TASKEI" as const,
        taskeiTaskId: t.id || t.taskId,
        title: t.name || t.title || "Untitled",
        severity: mapSeverity(t.priority || "SEV_4"),
        status: t.status || "Open",
        workflowStage: (state.workflowStage || "WP Triage") as WorkflowStage,
        assignee: t.assignee?.username || t.assignee || "Unassigned",
        waitingOn: (state.waitingOn || "none") as WaitingOn,
        createdAt,
        lastUpdatedAt: updatedAt,
        daysOpen: daysBetween(createdAt),
        daysStale: daysBetween(updatedAt),
        isEu,
        gdprDeadline: isEu
          ? new Date(new Date(createdAt).getTime() + 72 * 60 * 60 * 1000).toISOString()
          : undefined,
        description,
        tags: t.tags || [],
      };
    });
  } catch (error) {
    console.warn("Failed to fetch Taskei tasks:", error);
  }

  // Merge and de-duplicate (SIM-T takes priority if both have same ID)
  const allTickets = [...simtTickets];
  const simtIds = new Set(simtTickets.map((t) => t.ticketId));
  for (const t of taskeiTickets) {
    if (!simtIds.has(t.ticketId)) {
      allTickets.push(t);
    }
  }

  return allTickets;
}

/**
 * Fetch a single ticket's full detail.
 */
export async function fetchTicketDetail(ticketId: string): Promise<Ticket> {
  const response = await apiFetch<SimtTicket>(`/tickets/${ticketId}`);
  const localState = localStore.get<Record<string, Partial<Ticket>>>("ticketState", {});
  const state = localState[ticketId] || {};
  const description = response.description || "";
  const isEu = detectEu(description);

  return {
    ticketId: response.ticketId,
    source: "SIM_T",
    taskeiTaskId: state.taskeiTaskId,
    title: response.title,
    severity: mapSeverity(response.severity),
    status: response.status,
    workflowStage: (state.workflowStage || "WP Triage") as WorkflowStage,
    assignee: response.assignee?.value || "Unassigned",
    waitingOn: (state.waitingOn || "none") as WaitingOn,
    createdAt: response.createDate,
    lastUpdatedAt: response.lastUpdatedDate,
    daysOpen: daysBetween(response.createDate),
    daysStale: daysBetween(response.lastUpdatedDate),
    isEu,
    gdprDeadline: isEu
      ? new Date(new Date(response.createDate).getTime() + 72 * 60 * 60 * 1000).toISOString()
      : undefined,
    description,
  };
}

/**
 * Update local state for a ticket (waiting on, workflow stage, assignee).
 */
export function updateTicketState(
  ticketId: string,
  updates: Partial<Pick<Ticket, "waitingOn" | "workflowStage" | "assignee">>
): void {
  const allState = localStore.get<Record<string, Partial<Ticket>>>("ticketState", {});
  allState[ticketId] = { ...allState[ticketId], ...updates };
  localStore.set("ticketState", allState);
}

export { RESOLVER_GROUP, TASKEI_ROOM_ID, TASKEI_SPRINT_ID, PIRT_TEAM_MEMBERS };
